import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { refreshToySuggestions, TOY_CATALOG_CHANGED } from "@/hooks/useToySuggestions";

const PATH = "/sharing-tree/toy-suggestions/catalog";

export default function ToySuggestionCatalog({ canWrite = false }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="fb-card p-4 space-y-3" data-testid="toy-catalog">
      <button type="button" className="font-semibold text-left underline"
              aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        Shared toy suggestions {open ? "−" : "+"}
      </button>
      {open && <CatalogContents canWrite={canWrite} />}
    </section>
  );
}

function CatalogContents({ canWrite }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [reviewed, setReviewed] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(PATH);
      setEntries(data.entries);
      setError("");
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }
  useEffect(() => {
    let live = true;
    const controller = new AbortController();
    async function refresh() {
      try {
        const { data } = await api.get(PATH, { signal: controller.signal });
        if (live) { setEntries(data.entries); setError(""); }
      } catch (err) { if (live) setError(formatApiError(err)); }
      finally { if (live) setLoading(false); }
    }
    refresh();
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    window.addEventListener(TOY_CATALOG_CHANGED, refresh);
    return () => {
      live = false; controller.abort(); window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener(TOY_CATALOG_CHANGED, refresh);
    };
  }, []);

  async function save(id, body) {
    setBusy(true); setError(""); setMessage("");
    try {
      if (id) await api.patch(`${PATH}/${id}`, body);
      else await api.post(PATH, body);
      if (!id) setName("");
      if (id === editing) { setEditing(null); setReviewed(false); }
      setMessage("Suggestion saved. Existing requests have not changed.");
      refreshToySuggestions();
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  }

  const button = "px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50";
  const input = "px-3 py-2 rounded-lg border bg-white w-full";
  const visible = entries.filter((e) => (filter === "all" || e.status === filter)
    && e.name.toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="space-y-3">
      <p className="text-sm">
        Reusable public item names for all program years. Only approved suggestions appear in toy search.
        Renaming or retiring a suggestion never changes items already selected in a request.
      </p>
      <p className="text-xs" style={{ color: "var(--fb-text-muted)" }}>
        Enter concise item names only—no client names, contact information, medical details, notes, or historical request narratives.
        Custom requests stay private to the requesting member and are never automatically added here.
      </p>
      {!canWrite && <p className="text-sm">Read-only access. Only staff and admins can change suggestions.</p>}
      {canWrite && (
        <form className="flex gap-2 items-end" onSubmit={(e) => { e.preventDefault(); save(null, { name, status: "pending" }); }}>
          <label className="flex-1 text-sm">New public item name
            <input className={input} value={name} required maxLength={60}
                   data-testid="toy-catalog-new-name" onChange={(e) => setName(e.target.value)} />
          </label>
          <button type="submit" className={button} disabled={busy || !name.trim()}>Add draft</button>
        </form>
      )}
      <div className="flex gap-2 flex-wrap">
        <label className="flex-1 text-sm">Search suggestions
          <input className={input} value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label className="text-sm">Status
          <select className={input} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All</option><option value="active">Approved</option>
            <option value="pending">Drafts</option><option value="retired">Retired</option>
          </select>
        </label>
      </div>
      {loading && <p role="status">Loading suggestions…</p>}
      {error && <p role="alert" className="text-sm" style={{ color: "var(--fb-error)" }}>
        {error} <button type="button" className="underline" onClick={load}>Retry loading</button>
      </p>}
      {message && <p role="status" className="text-sm">{message}</p>}
      <ul className="max-h-80 overflow-y-auto divide-y">
        {visible.map((entry) => (
          <li key={entry.id} className="py-3 space-y-2" data-testid={`toy-catalog-entry-${entry.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="break-words">{entry.name} <span className="text-xs font-semibold">
                ({entry.status === "active" ? "Approved" : entry.status === "pending" ? "Draft" : "Retired"})
              </span></span>
              {canWrite && <div className="flex gap-2">
                <button type="button" className={button} disabled={busy} onClick={() => {
                  setEditing(entry.id); setEditName(entry.name); setReviewed(false); setMessage("");
                }}>{entry.status === "active" ? "Rename" : "Edit / approve"}</button>
                {entry.status !== "retired" && <button type="button" className={button} disabled={busy}
                  onClick={() => save(entry.id, { status: "retired" })}>Retire</button>}
              </div>}
            </div>
            {canWrite && editing === entry.id && (
              <form className="space-y-2" onSubmit={(e) => {
                e.preventDefault();
                save(entry.id, { name: editName, status: reviewed ? "active" : "pending", public_reviewed: reviewed });
              }}>
                <label className="block text-sm">Public item name (60 characters maximum)
                  <input className={input} required maxLength={60} value={editName}
                    onChange={(e) => { setEditName(e.target.value); setReviewed(false); }} />
                </label>
                <label className="flex gap-2 items-start text-sm">
                  <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
                  I have reviewed this name as a reusable public item. It contains no personal information or private notes.
                </label>
                <p className="text-xs">Saving without review keeps this name as a draft, hidden from toy search.</p>
                <div className="flex gap-2">
                  <button type="submit" className={button} disabled={busy}>{reviewed ? "Approve & save" : "Save draft"}</button>
                  <button type="button" className={button} disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </form>
            )}
          </li>
        ))}
      </ul>
      {!loading && !visible.length && <p className="text-sm">No suggestions match.</p>}
    </div>
  );
}
